import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type MouseEvent } from 'react'
import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router'
import {
  Plus,
  BookOpenText,
  MessagesSquare,
  CircleStop,
  Play,
  Paperclip,
  Activity,
  Archive,
  Clock3,
  Copy,
  UserRound,
} from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../../../../contexts/AuthContext'
import { Button } from '../../../../components/ui/button'
import { Alert, AlertDescription } from '../../../../components/ui/alert'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../../components/ui/dialog'
import { Skeleton } from '../../../../components/ui/skeleton'
import {
  ClassroomDialog,
  formatClassroomStatusLabel,
} from '../components'
import {
  assignClassTeachers,
  createClassroom,
  fetchClasses,
  fetchMembers,
  updateClassroomStatus,
} from '../../../../api/backend'
import { isDarkAtom } from '../../../../state/store'
import { getClassroomPalette } from '../../../../lib/classroom-theme'
import type { Classroom, StudentItem } from '../../../../api/backend'

export const CLASS_CARD_TITLE_CLASSNAME = 'max-w-[15ch] truncate font-heading leading-[1.04]'
export const CLASS_CARD_TOPIC_ROW_CLASSNAME = 'mt-2.5 flex min-h-[1.625rem] max-w-full flex-nowrap items-center gap-1.5 overflow-hidden'

export type ClassStatusActionCopyInput = {
  type: 'end' | 'reopen'
  intent?: 'open' | 'reopen'
}

export function getClassStatusActionCopy(action: ClassStatusActionCopyInput) {
  if (action.type === 'end') {
    return {
      title: 'End Class',
      body: 'This will move the class to past classes.',
      buttonLabel: 'End Class',
      successMessage: 'Class moved to past classes',
      failureAction: 'end',
    }
  }

  if (action.intent === 'open') {
    return {
      title: 'Open Class',
      body: 'This will open the class for students.',
      buttonLabel: 'Open Class',
      successMessage: 'Class opened',
      failureAction: 'open',
    }
  }

  return {
    title: 'Reopen Class',
    body: 'This will move the class back into current classes.',
    buttonLabel: 'Reopen Class',
    successMessage: 'Class reopened',
    failureAction: 'reopen',
  }
}

function getHeroForegroundMode(backgroundColor: string) {
  const match = backgroundColor.match(/hsl\(\s*\d+\s+\d+%\s+(\d+)%\s*\)/i)
  const lightness = match ? Number(match[1]) : 50
  return lightness >= 68 ? 'dark' : 'light'
}

function ClassCard({
  classroom,
  onEnd,
  onReopen,
  compact = false,
}: {
  classroom: Classroom
  onEnd: (classroom: Classroom) => void
  onReopen: (classroom: Classroom, actionLabel?: 'open' | 'reopen') => void
  compact?: boolean
}) {
  const isDark = useAtomValue(isDarkAtom)
  const navigate = useNavigate()
  const palette = getClassroomPalette(classroom)
  const cardHeroBg = isDark ? palette.dark : palette.light
  const heroForegroundMode = getHeroForegroundMode(cardHeroBg)
  const materialCount = classroom.materials?.length ?? 0
  const subtitle = classroom.teacherName || classroom.subject || 'No teacher assigned'
  const shellClass = isDark ? 'bg-[#181a1f] text-white' : 'bg-[#f2ede3] text-[#231f1d]'
  const lowerPanelClass = isDark ? 'bg-[#181a1f] text-white' : 'bg-[#f2ede3] text-[#231f1d]'
  const codePanelClass = isDark ? 'bg-[#293142] text-white' : 'bg-[#dfe5f1] text-[#231f1d]'
  const statTextClass = isDark ? 'text-white/60' : 'text-[#231f1d]/62'
  const footerTextClass = isDark ? 'text-white/48' : 'text-[#231f1d]/54'
  const actionButtonClass = isDark
    ? 'border-black/70 bg-white/8 text-white hover:bg-white/14'
    : 'border-black/20 bg-white/70 text-[#231f1d] hover:bg-white'

  const statusBadge = classroom.status === 'IN_PROGRESS'
    ? { Icon: Activity }
    : classroom.status === 'ENDED'
      ? { Icon: Archive }
      : { Icon: Clock3 }

  const creditUsed = classroom.usageCredits ?? 0
  const heroTitleClass = heroForegroundMode === 'dark' ? 'text-[#202114]' : 'text-white'
  const heroSubtitleClass = heroForegroundMode === 'dark' ? 'text-[#202114]/68' : 'text-white/64'
  const heroTopicClass = heroForegroundMode === 'dark'
    ? 'border-black/12 bg-black/8 text-[#202114]/80'
    : 'border-white/18 bg-white/10 text-white/84'
  const heroStatusClass = heroForegroundMode === 'dark'
    ? 'border-black/14 bg-white/38 text-[#202114]'
    : 'border-black/25 bg-black/12 text-white'

  const handleOpen = () => {
    navigate(`/admin/my-classes/${classroom.id}`)
  }

  const stopCardNavigation = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation()
  }

  const handleCopyCode = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    try {
      await navigator.clipboard.writeText(classroom.classCode)
      toast.success('Class code copied')
    } catch {
      toast.error('Failed to copy class code')
    }
  }

  return (
    <article
      className={`group grid aspect-[7/6] grid-rows-[39%_1fr] overflow-hidden rounded-[1.4rem] border-[3px] border-nb-border nb-shadow transition-transform duration-150 hover:-translate-y-1 ${shellClass} cursor-pointer`}
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleOpen()
        }
      }}
      role="link"
      tabIndex={0}
    >
      <div
        className="relative overflow-hidden px-6 pb-3 pt-4 sm:px-7"
        style={{ backgroundColor: cardHeroBg }}
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_30%),linear-gradient(180deg,rgba(0,0,0,0.06),rgba(0,0,0,0.18))]" />
        <div className="relative flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h2
              className={`${CLASS_CARD_TITLE_CLASSNAME} ${heroTitleClass} ${compact ? 'text-[1.35rem]' : 'text-[1.5rem]'}`}
              title={classroom.title}
            >
              {classroom.title}
            </h2>
            <div className={`mt-1.5 flex items-center gap-1.5 text-[0.82rem] ${heroSubtitleClass}`}>
              <UserRound className="size-3.5 shrink-0" />
              <p className="line-clamp-1">
                {subtitle}
              </p>
            </div>
            {classroom.topics.length > 0 ? (
              <div className={CLASS_CARD_TOPIC_ROW_CLASSNAME}>
                {classroom.topics.slice(0, compact ? 2 : 3).map((topic) => (
                  <span
                    key={topic}
                    className={`inline-flex min-w-0 max-w-[8.5rem] shrink items-center rounded-full border px-2.5 py-1 text-[0.62rem] font-medium ${heroTopicClass}`}
                    title={topic}
                  >
                    <span className="truncate">{topic}</span>
                  </span>
                ))}
              </div>
            ) : null}
          </div>

            <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[0.68rem] font-heading tracking-[0.08em] shadow-[0_2px_0_0_rgba(0,0,0,0.22)] ${heroStatusClass}`}>
              <statusBadge.Icon className="size-3.5" />
              {formatClassroomStatusLabel(classroom.status)}
            </span>
          </div>
      </div>

      <div className={`min-h-0 px-6 py-4 sm:px-7 ${lowerPanelClass}`}>
        <div
          className={`relative rounded-[0.8rem] border-[3px] border-black px-4 py-3 ${codePanelClass}`}
          onClick={stopCardNavigation}
          onMouseDown={stopCardNavigation}
          onKeyDown={stopCardNavigation}
        >
          <div className="min-w-0 pr-14">
            <p className={`${isDark ? 'text-white/42' : 'text-[#231f1d]/44'} text-[0.68rem] uppercase tracking-[0.34em]`}>
              Class Code
            </p>
            <p
              className={`mt-1.5 select-text font-bold leading-none tracking-[0.06em] ${compact ? 'text-[1.45rem]' : 'text-[1.55rem]'}`}
              style={{ fontFamily: '"DM Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
              title={classroom.classCode}
            >
              {classroom.classCode}
            </p>
          </div>
          <button
            type="button"
            className={`absolute right-3 top-1/2 inline-flex -translate-y-1/2 items-center justify-center rounded-full border px-2.5 py-2 opacity-0 transition-all duration-150 group-hover:opacity-100 focus:opacity-100 focus-visible:opacity-100 ${isDark ? 'border-white/14 bg-white/8 text-white hover:bg-white/14' : 'border-black/12 bg-white/70 text-[#231f1d] hover:bg-white'}`}
            onClick={handleCopyCode}
            aria-label="Copy class code"
            title="Copy class code"
          >
            <Copy className="size-3.5" />
          </button>
        </div>

        <div className={`mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[0.82rem] ${statTextClass}`}>
          <span className="inline-flex items-center gap-2">
            <MessagesSquare className="size-3.5" />
            {classroom.chatCount} {classroom.chatCount === 1 ? 'chat' : 'chats'}
          </span>
          <span className="inline-flex items-center gap-2">
            <Paperclip className="size-3.5" />
            {materialCount} {materialCount === 1 ? 'file' : 'files'}
          </span>
        </div>

        <div className="mt-4 flex items-end justify-between gap-3">
          <div className={`min-w-0 text-[0.82rem] ${footerTextClass}`}>
            {creditUsed} credits used
          </div>
          {classroom.status === 'ENDED' ? (
            <Button
              variant="neutral"
              size="sm"
              className={`h-8 px-3 text-[0.72rem] ${actionButtonClass}`}
              onMouseDown={(event) => {
                event.stopPropagation()
              }}
              onClick={(event) => {
                event.stopPropagation()
                onReopen(classroom)
              }}
            >
              <Play className="size-3.5" />
              Reopen
            </Button>
          ) : classroom.status === 'PREPARING' ? (
            <Button
              variant="neutral"
              size="sm"
              className={`h-8 px-3 text-[0.72rem] ${actionButtonClass}`}
              onMouseDown={(event) => {
                event.stopPropagation()
              }}
              onClick={(event) => {
                event.stopPropagation()
                onReopen(classroom, 'open')
              }}
            >
              <Play className="size-3.5" />
              Open
            </Button>
          ) : (
            <Button
              variant="neutral"
              size="sm"
              className={`h-8 px-3 text-[0.72rem] ${actionButtonClass}`}
              onMouseDown={(event) => {
                event.stopPropagation()
              }}
              onClick={(event) => {
                event.stopPropagation()
                onEnd(classroom)
              }}
            >
              <CircleStop className="size-3.5" />
              End
            </Button>
          )}
        </div>
      </div>
    </article>
  )
}

function ClassCardSkeleton() {
  const isDark = useAtomValue(isDarkAtom)

  return (
    <div
      className={`grid aspect-[7/6] grid-rows-[39%_1fr] overflow-hidden rounded-[1.4rem] border-[3px] border-nb-border nb-shadow ${
        isDark ? 'bg-[#181a1f]' : 'bg-[#f2ede3]'
      }`}
    >
      <div className={`relative overflow-hidden px-6 pb-3 pt-4 sm:px-7 ${isDark ? 'bg-[#384822]' : 'bg-[#c5d9a5]'}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className={`h-8 w-2/3 rounded-md ${isDark ? 'bg-white/18' : 'bg-white/55'}`} />
            <Skeleton className={`h-4 w-1/3 rounded-md ${isDark ? 'bg-white/14' : 'bg-white/40'}`} />
            <div className="flex flex-wrap gap-1.5 pt-1">
              <Skeleton className={`h-5 w-16 rounded-full ${isDark ? 'bg-white/12' : 'bg-white/35'}`} />
              <Skeleton className={`h-5 w-20 rounded-full ${isDark ? 'bg-white/12' : 'bg-white/35'}`} />
            </div>
          </div>
          <Skeleton className={`h-8 w-28 rounded-full ${isDark ? 'bg-black/20' : 'bg-black/10'}`} />
        </div>
      </div>

      <div className={`min-h-0 px-6 py-4 sm:px-7 ${isDark ? 'bg-[#181a1f]' : 'bg-[#f2ede3]'}`}>
        <div className={`rounded-[0.8rem] border-[3px] border-black px-4 py-3 ${isDark ? 'bg-[#293142]' : 'bg-[#dfe5f1]'}`}>
          <Skeleton className={`h-3 w-24 rounded-md ${isDark ? 'bg-white/14' : 'bg-black/10'}`} />
          <Skeleton className={`mt-2 h-10 w-40 rounded-md ${isDark ? 'bg-white/16' : 'bg-black/12'}`} />
        </div>

        <div className="mt-4 flex items-center gap-4">
          <Skeleton className={`h-4 w-20 rounded-md ${isDark ? 'bg-white/12' : 'bg-black/10'}`} />
          <Skeleton className={`h-4 w-18 rounded-md ${isDark ? 'bg-white/12' : 'bg-black/10'}`} />
        </div>

        <div className="mt-4 flex items-end justify-between gap-3">
          <div className="space-y-2">
            <Skeleton className={`h-4 w-24 rounded-md ${isDark ? 'bg-white/12' : 'bg-black/10'}`} />
          </div>
          <Skeleton className={`h-8 w-20 rounded-md ${isDark ? 'bg-white/12' : 'bg-black/10'}`} />
        </div>
      </div>
    </div>
  )
}

export default function MyClassesPage() {
  const { getAccessToken } = useAuth()
  const [classes, setClasses] = useState<Classroom[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [teachers, setTeachers] = useState<StudentItem[]>([])
  const [pendingAction, setPendingAction] = useState<(ClassStatusActionCopyInput & {
    classroom: Classroom
  }) | null>(null)

  const load = useCallback(() => {
    const token = getAccessToken()
    if (!token) return
    setIsLoading(true)
    setError(null)
    Promise.all([
      fetchClasses(token),
      fetchMembers(token, 'TEACHER', 0, 100),
    ])
      .then(([classrooms, teacherResponse]) => {
        setClasses(classrooms)
        setTeachers(teacherResponse.content)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load classes')
      })
      .finally(() => setIsLoading(false))
  }, [getAccessToken])

  useEffect(() => {
    load()
  }, [load])

  const handleCreate = async (payload: Parameters<typeof createClassroom>[1] & { teacherUserIds: string[] }) => {
    const token = getAccessToken()
    if (!token) return
    try {
      const { teacherUserIds, ...classPayload } = payload
      const created = await createClassroom(token, classPayload)
      if (!created?.id) {
        throw new Error('Class was created but no class id was returned')
      }
      if (teacherUserIds.length > 0) {
        await assignClassTeachers(token, created.id, teacherUserIds)
      }
      setClasses((prev) => [created, ...prev])
      setShowCreateDialog(false)
      toast.success('Class created')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create class')
    }
  }

  const handleEnd = async (classroom: Classroom) => {
    setPendingAction({ type: 'end', classroom })
  }

  const handleReopen = async (classroom: Classroom, actionLabel: 'open' | 'reopen' = 'reopen') => {
    setPendingAction({ type: 'reopen', intent: actionLabel, classroom })
  }

  const handleConfirmStatusChange = async () => {
    const token = getAccessToken()
    if (!token || !pendingAction) return
    const { classroom, type } = pendingAction
    const actionCopy = getClassStatusActionCopy(pendingAction)
    try {
      const updated = await updateClassroomStatus(token, classroom.id, {
        status: type === 'end' ? 'ENDED' : 'IN_PROGRESS',
      })
      setClasses((prev) => prev.map((item) => (item.id === updated.id ? updated : item)))
      setPendingAction(null)
      toast.success(actionCopy.successMessage)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to ${actionCopy.failureAction} class`)
    }
  }

  const pendingActionCopy = pendingAction ? getClassStatusActionCopy(pendingAction) : null

  return (
    <div className="px-4 py-6 sm:px-6">
      <div className="mx-auto flex max-w-[1180px] flex-col gap-7">
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center gap-3">
              <span>{error}</span>
              <button className="ml-auto underline underline-offset-2 hover:no-underline" onClick={load}>
                Retry
              </button>
            </AlertDescription>
          </Alert>
        )}

        <section>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-[42rem]">
              <h1 className="font-heading text-[2.25rem] leading-tight text-foreground sm:text-[2.35rem]">
                My Classes
              </h1>
              <p className="mt-2 max-w-[42rem] text-[0.92rem] leading-6 text-foreground/62">
                Open a class, share its code with students, and manage people or classwork inside.
              </p>
            </div>
            <Button className="w-full sm:w-auto" onClick={() => setShowCreateDialog(true)}>
              <Plus className="size-4" />
              Create Class
            </Button>
          </div>
        </section>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <ClassCardSkeleton key={index} />
            ))}
          </div>
        ) : (
          <section>
            {classes.length === 0 ? (
              <div className="rounded-[1.5rem] border-[3px] border-dashed border-nb-border bg-bg p-10 text-[0.9rem] text-foreground/50">
                No classes yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                {classes.map((classroom) => (
                  <ClassCard
                    key={classroom.id}
                    classroom={classroom}
                    onEnd={handleEnd}
                    onReopen={handleReopen}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        <ClassroomDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          teachers={teachers}
          onSubmit={handleCreate}
        />

        <Dialog
          open={!!pendingAction}
          onOpenChange={(next) => !next && setPendingAction(null)}
        >
          <DialogContent className="max-w-md" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                {pendingActionCopy?.title}
              </DialogTitle>
            </DialogHeader>

            <div className="text-sm text-foreground/70">
              {pendingAction && pendingActionCopy ? (
                <>
                  {pendingActionCopy.body}{' '}
                  <span className="font-heading text-foreground">{pendingAction.classroom.title}</span>
                </>
              ) : null}
            </div>

            <DialogFooter>
              <Button variant="neutral" size="sm" onClick={() => setPendingAction(null)}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleConfirmStatusChange}>
                {pendingActionCopy?.buttonLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
